/**
 * 总体 P/R/F1 KPI 卡（mock v3 第三段）。
 *
 * 第一版只算召回率，precision / f1 恒 null → 卡片显示 EMPTY 并挂一句说明，而不是
 * 隐藏这两张卡：留着位置能表达「这两个指标存在但本期未算」，隐藏则让人以为报表
 * 只关心召回率。
 *
 * ``delta`` 传入时（对比视图）在数值右侧挂 pp 差；单 job 视图不传。
 *
 * IoU 卡片按计数单位切换口径：``cell``（OCC 格子）显示「IoU」= Σtp/(Σtp+Σfp+Σfn)，
 * 另加「占据召回」（扩展计数 tp_occupied/gt，有才显示）；其余（``box``，OD 目标框）
 * 显示「平均 IoU」= Σiou_sum/Σtp。
 */
import { Tooltip } from 'antd';

import type { PerceptionDeltaMetrics, PerceptionMetrics } from '@/apis/simulation';

import { deltaColor, EMPTY, fmtCount, fmtPp, fmtRatio, isCellUnit, unitLabel } from '../format';

interface KpiProps {
  label: string;
  /** 0~1 小数 */
  value: number | null;
  /** 已是 pp 的差值 */
  delta?: number | null;
  sub?: string;
  hint?: string;
}

function Kpi({ label, value, delta, sub, hint }: KpiProps) {
  return (
    <div className="pe-kpi">
      <div className="pe-kpi-label">
        {hint ? <Tooltip title={hint}>{label}</Tooltip> : label}
      </div>
      <div className="pe-kpi-value">
        {fmtRatio(value)}
        {delta !== undefined && delta !== null && (
          <span className="pe-kpi-delta" style={{ color: deltaColor(delta) }}>
            {fmtPp(delta)}
          </span>
        )}
      </div>
      <div className="pe-kpi-sub">{sub || ' '}</div>
    </div>
  );
}

interface OverallKpisProps {
  overall: PerceptionMetrics;
  delta?: PerceptionDeltaMetrics | null;
  /** 计数单位：决定 IoU 卡片的口径；不传按 box（OD）处理 */
  unit?: string | null;
}

/** 第一版未计算的指标共用的说明文案。 */
const NOT_COMPUTED = '第一版只算召回率：算法侧未上报 fp，精确率 / F1 不可算，显示为 —（不是 0）';

export default function OverallKpis({ overall, delta, unit }: OverallKpisProps) {
  const gt = overall.gt;
  const tp = overall.tp;
  const cell = isCellUnit(unit);
  const tpOccupied = overall.extraCounts?.tp_occupied;
  const fp = overall.fp;
  const fn = overall.fn;
  return (
    <div className="pe-kpis">
      <Kpi
        label="召回率 Recall"
        value={overall.recall}
        delta={delta?.recall}
        sub={
          gt === null || tp === null
            ? EMPTY
            : `Σtp/Σgt = ${fmtCount(tp)}/${fmtCount(gt)}`
        }
        hint="全部 case 合并口径：Σtp/Σgt，不是各 case 召回率的平均（case 的 GT 数差两个数量级）"
      />
      <Kpi
        label="精确率 Precision"
        value={overall.precision}
        delta={delta?.precision}
        sub={overall.precision === null ? '本期未计算' : undefined}
        hint={NOT_COMPUTED}
      />
      <Kpi
        label="F1"
        value={overall.f1}
        delta={delta?.f1}
        sub={overall.f1 === null ? '本期未计算' : undefined}
        hint={NOT_COMPUTED}
      />
      {cell ? (
        <Kpi
          label="IoU"
          value={overall.iou ?? null}
          delta={delta?.iou}
          sub={
            tp === null || fp === null || fn === null
              ? EMPTY
              : `Σtp/(Σtp+Σfp+Σfn) = ${fmtCount(tp)}/${fmtCount(tp + fp + fn)}`
          }
          hint="格子 IoU：各 case 计数求和后重算 Σtp/(Σtp+Σfp+Σfn)，不是各 case IoU 的平均"
        />
      ) : (
        <Kpi
          label="平均 IoU"
          value={overall.meanIou}
          delta={delta?.meanIou}
          sub={
            overall.meanIou === null
              ? '本期未计算'
              : `Σiou/Σtp = ${overall.iouSum?.toFixed(2)}/${fmtCount(tp)}`
          }
          hint="匹配对 IoU 的算术平均：Σ(iou_sum)/Σ(tp)，tp=0 时为 null"
        />
      )}
      {cell && tpOccupied !== undefined && (
        <Kpi
          label="占据召回"
          value={tpOccupied === null || !gt ? null : tpOccupied / gt}
          sub={
            tpOccupied === null || gt === null
              ? EMPTY
              : `仿真给出任意类别 ${fmtCount(tpOccupied)}/${fmtCount(gt)}`
          }
          hint="真值有标注的格子里，仿真给出任意非空类别的比例（不要求类别一致）"
        />
      )}
      <div className="pe-kpi">
        <div className="pe-kpi-label">
          <Tooltip title="贡献该组指标的 case 数 = 漏斗末级">参与 case 数</Tooltip>
        </div>
        <div className="pe-kpi-value">{fmtCount(overall.caseCount)}</div>
        <div className="pe-kpi-sub">
          GT {cell ? '格子' : '目标'} {fmtCount(gt)} / 命中 {fmtCount(tp)}
          {unit ? ` · 单位 ${unitLabel(unit)}` : ''}
        </div>
      </div>
    </div>
  );
}
